import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const hours = Array.from({ length: 24 }, (_, i) => i);

const SchedulerContainer = styled(Paper)(({ theme }) => ({
    padding: '15px',
    borderRadius: '12px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    overflow: 'auto',
    maxHeight: '400px',
    '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
        borderRadius: '8px',
        backgroundColor: 'rgba(99, 87, 129, 0.3)',
        '&:hover': {
            backgroundColor: 'rgba(99, 87, 129, 0.5)',
        },
    },
}));

const GridContainer = styled(Box)({
    display: 'inline-block',
    minWidth: '100%',
    userSelect: 'none',
});

const GridTable = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '45px repeat(24, 30px)',
    gap: '2px',
    marginTop: '8px',
});

const TimeHeader = styled(Box)(({ theme }) => ({
    fontSize: '10px',
    fontWeight: '600',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    padding: '6px 2px',
    backgroundColor: 'transparent',
    position: 'relative',
}));

const DayLabel = styled(Box)(({ theme }) => ({
    fontSize: '12px',
    fontWeight: '600',
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '8px',
    backgroundColor: 'transparent',
}));

const TimeCell = styled(Box)(({ selected, theme }) => ({
    height: '24px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    backgroundColor: selected ? '#63578160' : '#f5f5f5',
    border: `1.5px solid ${selected ? '#483F5E60' : '#e0e0e0'}`,
    '&:hover': {
        backgroundColor: selected ? '#483F5E' : '#e8e8e8',
        transform: 'scale(1.05)',
        zIndex: 10,
        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
    },
}));

const LegendContainer = styled(Box)({
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    flexWrap: 'wrap',
});

const LegendItem = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
});

const LegendBox = styled(Box)(({ color }) => ({
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    backgroundColor: color,
    border: '2px solid #e0e0e0',
}));

const SchedulerTable = ({ value, onChange, label }) => {
    const [schedule, setSchedule] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [dragMode, setDragMode] = useState(null); // 'select' or 'deselect'
    const containerRef = useRef(null);

    // Initialize schedule from value
    useEffect(() => {
        try {
            
            if (!value) {
                // Don't reset to empty object if value is falsy - keep default values
                return;
            }

            let parsedValue = value;
            
            // Parse if it's a JSON string
            if (typeof value === 'string') {
                parsedValue = JSON.parse(value);
            }

            // If it's an array (168 elements), convert to object format
            if (Array.isArray(parsedValue)) {
                const scheduleObj = {};
                parsedValue.forEach((isActive, index) => {
                    if (isActive === 1 || isActive === true) {
                        const day = Math.floor(index / 24);
                        const hour = index % 24;
                        if (day >= 0 && day < 7 && hour >= 0 && hour < 24) {
                            scheduleObj[`${day}-${hour}`] = true;
                        }
                    }
                });
                setSchedule(scheduleObj);
            } else if (typeof parsedValue === 'object') {
                // It's already in object format
                setSchedule(parsedValue);
            }
        } catch (e) {
            console.error('Error parsing schedule value:', e);
            // Don't reset to empty object on parse error - keep default values
        }
    }, [value]);

    const getCellKey = (day, hour) => `${day}-${hour}`;

    const isSelected = (day, hour) => {
        return schedule && schedule[getCellKey(day, hour)] === true;
    };

    const toggleCell = (day, hour, forceState = null) => {
        const key = getCellKey(day, hour);
        const newSchedule = { ...(schedule || {}) };
        
        if (forceState !== null) {
            if (forceState) {
                newSchedule[key] = true;
            } else {
                delete newSchedule[key];
            }
        } else {
            if (newSchedule[key]) {
                delete newSchedule[key];
            } else {
                newSchedule[key] = true;
            }
        }
        
        setSchedule(newSchedule);
        onChange(JSON.stringify(newSchedule));
    };

    const handleMouseDown = (day, hour) => {
        setIsDragging(true);
        const currentState = isSelected(day, hour);
        setDragMode(currentState ? 'deselect' : 'select');
        toggleCell(day, hour);
    };

    const handleMouseEnter = (day, hour) => {
        if (isDragging) {
            const shouldSelect = dragMode === 'select';
            const currentState = isSelected(day, hour);
            if (currentState !== shouldSelect) {
                toggleCell(day, hour, shouldSelect);
            }
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setDragMode(null);
    };

    // Add global mouse up listener
    useEffect(() => {
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    const selectedCount = Object.keys(schedule || {}).length;
    const totalCells = 7 * 24;

    return (
        <Box>
            <Typography 
                variant="subtitle1" 
                sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <span>{label || 'Schedule'}</span>
                <Typography 
                    variant="caption" 
                    sx={{ 
                        color: 'text.secondary',
                        backgroundColor: '#f5f5f5',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: 500
                    }}
                >
                    {selectedCount} / {totalCells} time slots selected
                </Typography>
            </Typography>

            <LegendContainer>
                <LegendItem>
                    <LegendBox color="#635781" />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Available
                    </Typography>
                </LegendItem>
                <LegendItem>
                    <LegendBox color="#f5f5f5" />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Unavailable
                    </Typography>
                </LegendItem>
            </LegendContainer>

            <SchedulerContainer ref={containerRef}>
                <GridContainer sx={{ justifyContent: 'center', alignItems: 'center' ,display: 'flex', padding: '10px'}}>
                    <GridTable>
                        {/* Empty corner cell */}
                        <Box />
                        
                        {/* Hour headers */}
                        {hours.map(hour => (
                            <TimeHeader key={hour}>
                                {hour}
                            </TimeHeader>
                        ))}
                        
                        {/* Day rows */}
                        {days.map((day, dayIndex) => (
                            <React.Fragment key={day}>
                                <DayLabel>{day}</DayLabel>
                                {hours.map(hour => (
                                    <TimeCell
                                        key={`${day}-${hour}`}
                                        selected={isSelected(dayIndex, hour)}
                                        onMouseDown={() => handleMouseDown(dayIndex, hour)}
                                        onMouseEnter={() => handleMouseEnter(dayIndex, hour)}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </GridTable>
                </GridContainer>
            </SchedulerContainer>

            <Typography 
                variant="caption" 
                sx={{ 
                    display: 'block', 
                    mt: 1.5, 
                    color: 'text.secondary',
                    fontStyle: 'italic'
                }}
            >
                Click and drag to select/deselect time slots
            </Typography>
        </Box>
    );
};

export default SchedulerTable;

